import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';

import type { VehicleTrackPoint, VehicleTripTrack } from '@/api/vehiclesApi';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const VEHICLE_TRIP_ROUTE_SOURCE_ID = 'orbit-vehicle-trip-route-source';

/** The trip whose track is currently drawn on the map, or null for none. */
export interface DisplayedTripTrack {
    tripId: string;
    track: VehicleTripTrack;
}

const ORIGIN_BLUE = '#007CFF';

function pointFeature(point: VehicleTrackPoint, part: string): Feature {
    return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
        properties: { part },
    };
}

/**
 * §10.3 made concrete: the line is drawn exclusively from the API's `route` (genuine
 * provider points, present only when route_available). Summary coordinates and isolated
 * genuine points render as markers only — a trip without a real route never gets a line.
 */
export function buildTrackFeatureCollection(track: VehicleTripTrack): FeatureCollection {
    const features: Feature[] = [];
    if (track.route && track.route.coordinates.length >= 2) {
        features.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: track.route.coordinates.map((c) => [c[0], c[1]]) },
            properties: { part: 'route' },
        });
    }
    for (const point of track.isolatedPoints ?? []) {
        features.push(pointFeature(point, 'genuine'));
    }
    if (track.startPoint) features.push(pointFeature(track.startPoint, 'start'));
    if (track.endPoint) features.push(pointFeature(track.endPoint, 'end'));
    return { type: 'FeatureCollection', features };
}

export function trackBounds(collection: FeatureCollection): maplibregl.LngLatBounds | null {
    const bounds = new maplibregl.LngLatBounds();
    let hasCoordinates = false;
    for (const feature of collection.features) {
        if (feature.geometry.type === 'Point') {
            bounds.extend(feature.geometry.coordinates as [number, number]);
            hasCoordinates = true;
        } else if (feature.geometry.type === 'LineString') {
            for (const coordinate of feature.geometry.coordinates) {
                bounds.extend(coordinate as [number, number]);
                hasCoordinates = true;
            }
        }
    }
    return hasCoordinates ? bounds : null;
}

function registerTrackSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection): void {
    try {
        const source = mlMap.getSource(VEHICLE_TRIP_ROUTE_SOURCE_ID);
        if (source) {
            (source as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(VEHICLE_TRIP_ROUTE_SOURCE_ID, { type: 'geojson', data });
        }
    } catch {
        return;
    }

    try {
        if (!mlMap.getLayer(ORBIT_LAYER_IDS.vehicleTripRoute)) {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.vehicleTripRoute,
                    type: 'line',
                    source: VEHICLE_TRIP_ROUTE_SOURCE_ID,
                    filter: ['==', ['get', 'part'], 'route'],
                    layout: { 'line-cap': 'round', 'line-join': 'round' },
                    paint: { 'line-color': ORIGIN_BLUE, 'line-width': 4, 'line-opacity': 0.85 },
                },
                'interaction',
            );
        }
        if (!mlMap.getLayer(ORBIT_LAYER_IDS.vehicleTripRoutePoints)) {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.vehicleTripRoutePoints,
                    type: 'circle',
                    source: VEHICLE_TRIP_ROUTE_SOURCE_ID,
                    filter: ['!=', ['get', 'part'], 'route'],
                    paint: {
                        // summary start/end read slightly larger than lone genuine points
                        'circle-radius': ['match', ['get', 'part'], 'genuine', 5, 6],
                        'circle-color': ['match', ['get', 'part'], 'end', '#1e40af', ORIGIN_BLUE],
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                    },
                },
                'interaction',
            );
        }
    } catch {
        // Progressive enhancement — layer failures must not break the map.
    }
}

/**
 * Draws the selected trip's track (route line and/or location markers) and fits the view
 * to it. Clearing `displayed` empties the source; the layers stay registered for reuse.
 */
export function useVehicleTripRouteLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    displayed: DisplayedTripTrack | null,
): void {
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const data = displayed ? buildTrackFeatureCollection(displayed.track) : EMPTY_FEATURE_COLLECTION;

        const register = () => {
            registerTrackSourceAndLayers(mlMap, data);
        };
        register();
        // base-map switches reload all sources and layers; re-register to survive them
        mlMap.on('style.load', register);

        if (displayed) {
            const bounds = trackBounds(data);
            if (bounds) mlMap.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 600 });
        }

        return () => {
            mlMap.off('style.load', register);
            setGeoJsonSourceData(mlMap, VEHICLE_TRIP_ROUTE_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
        };
    }, [mapRef, isMapLoaded, displayed]);
}
